<template>
  <div class="position-relative">
    <template v-if="isDataLoading">
      <slot name="loading">
        Загрузка...
      </slot>
    </template>
    <template v-else>
      <template v-if="isDataComputing">
        <slot name="computing">
          <div class="position-absolute w-100 h-100 text-center" style="z-index: 1;">
            <div class="position-sticky d-inline-block mt-5 p-3" style="top: 0;">
              Загрузка значений таблицы...
            </div>
          </div>
        </slot>
      </template>

      <div v-if="data.length === 0 || (rows.length === 0 && cols.length === 0)" class="alert alert-warning" role="alert">
        {{ noDataWarningText }}
      </div>

      <div v-else-if="cols.length && rows.length" class="table-responsive">
        <table id="pivot-table" class="table table-bordered" :aria-busy="isDataLoading || isDataComputing">
          <!-- Table header -->
          <thead>
            <template v-if="isColumnValues">
              <tr>
                <template
                  v-for="(colField, colFieldIndex) in safeValueColumns"
                >
                  <!-- Top left dead zone -->
                  <th
                    :colspan="rowHeaderSize"
                    v-if="colFieldIndex === 0 && rowHeaderSize > 0"
                  />
                  <!-- Column headers -->
                  <th>
                    {{ colField.label }}
                  </th>
                </template>
                <th
                  v-for="extra in columnValuesMetricRowTotalColumns"
                  :key="'cvh-' + extra.key"
                  class="text-right align-middle font-weight-bold"
                >
                  {{ extra.headerLabel }}
                </th>
                <!-- Align with body row footer columns (classic layout) -->
                <th
                  v-if="rowFooterSize > 0"
                  :colspan="rowFooterSize"
                />
              </tr>
            </template>
            <template
              v-for="(colField, colFieldIndex) in internalColFields"
              v-if="(colField.showHeader === undefined || colField.showHeader) && !isColumnValues"
            >
              <!-- Multiple slots -->
              <template v-if="colField.headerSlotNames">
                <tr v-for="(headerSlotName, headerSlotNameIndex) in colField.headerSlotNames">
                  <!-- Top left dead zone -->
                  <th
                    v-if="colFieldIndex === firstColFieldHeaderIndex && rowHeaderSize > 0 && headerSlotNameIndex === 0"
                    :colspan="rowHeaderSize"
                    :rowspan="colHeaderSize"
                  />
                  <!-- Column headers -->
                  <th
                    v-for="(col, colIndex) in sortedCols"
                    v-if="spanSize(sortedCols, colIndex, colFieldIndex) !== 0"
                    :key="JSON.stringify(col)"
                    :colspan="spanSize(sortedCols, colIndex, colFieldIndex)"
                  >
                    <slot :name="headerSlotName" :value="col[colFieldIndex]">
                      Missing slot <code>{{ headerSlotName }}</code>
                    </slot>
                  </th>
                  <template v-if="isFirstClassicPivotHeaderRow(colFieldIndex, headerSlotNameIndex, colField)">
                    <th
                      v-for="rt in rowFieldsWithTotalsForRight"
                      :key="'rth-' + rt.field.key"
                      :rowspan="Math.max(colHeaderSize, 1)"
                      class="text-right align-middle font-weight-bold"
                    >
                      {{ totalByFieldLabelPrefix }}{{ rt.field.label }}
                    </th>
                    <th
                      v-if="showGrandTotalColumn"
                      :rowspan="Math.max(colHeaderSize, 1)"
                      class="text-right align-middle font-weight-bold"
                    >
                      {{ grandTotalLabel }}
                    </th>
                  </template>
                  <!-- Top right dead zone -->
                  <th
                    v-if="colFieldIndex === firstColFieldHeaderIndex && rowFooterSize > 0 && headerSlotNameIndex === 0"
                    :colspan="rowFooterSize"
                    :rowspan="colFooterSize"
                  />
                </tr>
              </template>
              <!-- Single slot/no slot -->
              <tr v-else>
                <!-- Top left dead zone -->
                <th
                  v-if="colFieldIndex === firstColFieldHeaderIndex && rowHeaderSize > 0"
                  :colspan="rowHeaderSize"
                  :rowspan="colHeaderSize"
                />
                <!-- Column headers -->
                <th
                  v-for="(col, colIndex) in sortedCols"
                  v-if="spanSize(sortedCols, colIndex, colFieldIndex) !== 0"
                  :key="JSON.stringify(col)"
                  :colspan="spanSize(sortedCols, colIndex, colFieldIndex)"
                >
                  <slot v-if="colField.headerSlotName" :name="colField.headerSlotName" :value="col[colFieldIndex]">
                    Missing slot <code>{{ colField.headerSlotName }}</code>
                  </slot>
                  <template v-else>
                    {{ col[colFieldIndex] }}
                  </template>
                </th>
                <template v-if="isFirstClassicPivotHeaderRow(colFieldIndex, 0, colField)">
                  <th
                    v-for="rt in rowFieldsWithTotalsForRight"
                    :key="'rth2-' + rt.field.key"
                    :rowspan="Math.max(colHeaderSize, 1)"
                    class="text-right align-middle font-weight-bold"
                  >
                    {{ totalByFieldLabelPrefix }}{{ rt.field.label }}
                  </th>
                  <th
                    v-if="showGrandTotalColumn"
                    :rowspan="Math.max(colHeaderSize, 1)"
                    class="text-right align-middle font-weight-bold"
                  >
                    {{ grandTotalLabel }}
                  </th>
                </template>
                <!-- Top right dead zone -->
                <th
                  v-if="colFieldIndex === firstColFieldHeaderIndex && rowFooterSize > 0"
                  :colspan="rowFooterSize"
                  :rowspan="colFooterSize"
                />
              </tr>
            </template>
          </thead>

          <!-- Table body -->
          <tbody>
            <tr v-for="(row, rowIndex) in sortedRows" :key="JSON.stringify(row)">
              <!-- Row headers -->
              <template
                v-for="(rowField, rowFieldIndex) in internalRowFields"
                v-if="(rowField.showHeader === undefined || rowField.showHeader) && spanSize(sortedRows, rowIndex, rowFieldIndex) !== 0"
              >
                <!-- Multiple slots -->
                <template v-if="rowField.headerSlotNames">
                  <th
                    v-for="headerSlotName in rowField.headerSlotNames"
                    :rowspan="spanSize(sortedRows, rowIndex, rowFieldIndex)"
                  >
                    <slot :name="headerSlotName" :value="row[rowFieldIndex]">
                      Missing slot <code>{{ headerSlotName }}</code>
                    </slot>
                  </th>
                </template>
                <!-- Single slot/no slot -->
                <th v-else :rowspan="spanSize(sortedRows, rowIndex, rowFieldIndex)">
                  <slot v-if="rowField.headerSlotName" :name="rowField.headerSlotName" :value="row[rowFieldIndex]">
                    Missing slot <code>{{ rowField.headerSlotName }}</code>
                  </slot>
                  <template v-else>
                    {{ row[rowFieldIndex] }}
                  </template>
                </th>
              </template>
              <!-- Values -->
              <template v-if="isColumnValues">
                <td
                  v-for="col in safeValueColumns"
                  :key="col.key"
                  class="text-right"
                >
                  <slot v-if="$scopedSlots.value" name="value" :value="value(row, [])" :row="Object.values(row)" :col="col" :isColumnValues="true" />
                  <template v-else>
                    {{ col.label }}
                  </template>
                </td>
                <td
                  v-for="extra in columnValuesMetricRowTotalColumns"
                  v-if="spanSize(sortedRows, rowIndex, extra.rowFieldIndex) !== 0"
                  :key="'cvb-' + rowIndex + '-' + extra.key"
                  :rowspan="spanSize(sortedRows, rowIndex, extra.rowFieldIndex)"
                  class="text-right align-middle"
                >
                  <template
                    v-if="$scopedSlots.value && shouldRenderFooterValueSlot(columnValuesMetricRowTotalRaw(row, extra.rowFieldIndex))"
                  >
                    <slot
                      name="value"
                      :value="columnValuesMetricRowTotalRaw(row, extra.rowFieldIndex)"
                      :row="Object.values(sortedRows[rowIndex])"
                      :col="extra.metric"
                      :isColumnValues="true"
                    />
                  </template>
                  <template v-else>
                    {{ formatFooterScalar(columnValuesMetricRowTotalRaw(row, extra.rowFieldIndex)) }}
                  </template>
                </td>
              </template>
              <template v-else>
                <td
                  v-for="col in sortedCols"
                  :key="JSON.stringify(col)"
                  class="text-right"
                >
                  <slot v-if="$scopedSlots.value" name="value" :value="value(row, col)" :row="Object.values(row)" :col="Object.values(col)" :isColumnValues="false" />
                  <template v-else>
                    {{ value(row, col) }}
                  </template>
                </td>
              </template>
              <template v-if="!isColumnValues && valuesHashTable && bodyRowTotalsMatrix[rowIndex]">
                <td
                  v-for="rtc in bodyRowTotalsMatrix[rowIndex].rowTotals"
                  v-if="spanSize(sortedRows, rowIndex, rtc.rowFieldIndex) !== 0"
                  :key="'rtb-' + rowIndex + '-' + rtc.key"
                  :rowspan="spanSize(sortedRows, rowIndex, rtc.rowFieldIndex)"
                  class="text-right align-middle"
                >
                  <template v-if="$scopedSlots.value && shouldRenderFooterValueSlot(rtc.rawValue)">
                    <slot
                      name="value"
                      :value="rtc.rawValue"
                      :row="Object.values(sortedRows[rowIndex])"
                      :col="[]"
                      :isColumnValues="false"
                    />
                  </template>
                  <template v-else>
                    {{ formatFooterScalar(rtc.rawValue) }}
                  </template>
                </td>
                <td
                  v-if="showGrandTotalColumn"
                  :key="'gtb-' + rowIndex"
                  class="text-right align-middle"
                >
                  <template v-if="$scopedSlots.value && shouldRenderFooterValueSlot(bodyRowTotalsMatrix[rowIndex].grandRowRaw)">
                    <slot
                      name="value"
                      :value="bodyRowTotalsMatrix[rowIndex].grandRowRaw"
                      :row="Object.values(sortedRows[rowIndex])"
                      :col="[]"
                      :isColumnValues="false"
                    />
                  </template>
                  <template v-else>
                    {{ formatFooterScalar(bodyRowTotalsMatrix[rowIndex].grandRowRaw) }}
                  </template>
                </td>
              </template>
              <!-- Row footers (if slots are provided) -->
              <template
                v-for="(rowField, rowFieldIndex) in internalRowFieldsReverse"
                v-if="rowField.showFooter && spanSize(rows, rowIndex, internalRowFields.length - rowFieldIndex - 1) !== 0"
              >
                <!-- Multiple slots -->
                <template v-if="rowField.footerSlotNames">
                  <th
                    v-for="footerSlotName in rowField.footerSlotNames"
                    :rowspan="spanSize(rows, rowIndex, internalRowFields.length - rowFieldIndex - 1)"
                  >
                    <slot :name="footerSlotName" :value="row[internalRowFields.length - rowFieldIndex - 1]">
                      Missing slot <code>{{ footerSlotName }}</code>
                    </slot>
                  </th>
                </template>
                <!-- Single slot/no slot -->
                <th v-else :rowspan="spanSize(rows, rowIndex, internalRowFields.length - rowFieldIndex - 1)">
                  <slot v-if="rowField.footerSlotName" :name="rowField.footerSlotName" :value="row[internalRowFields.length - rowFieldIndex - 1]">
                    Missing slot <code>{{ rowField.footerSlotName }}</code>
                  </slot>
                  <template v-else>
                    {{ row[internalRowFields.length - rowFieldIndex - 1] }}
                  </template>
                </th>
              </template>
            </tr>
          </tbody>

          <!-- Table footer -->
          <tfoot>
            <template
              v-for="(colField, colFieldIndex) in internalColFieldsReverse"
              v-if="colField.showFooter"
            >
              <!-- Multiple slots -->
              <template v-if="colField.footerSlotNames">
                <tr v-for="(footerSlotName, footerSlotNameIndex) in colField.footerSlotNames">
                  <!-- Bottom left dead zone -->
                  <th
                    v-if="colFieldIndex === firstColFieldFooterIndex && rowHeaderSize > 0 && footerSlotNameIndex === 0"
                    :colspan="rowHeaderSize"
                    :rowspan="colHeaderSize"
                  />
                  <!-- Column footers -->
                  <th
                    v-for="(col, colIndex) in sortedCols"
                    v-if="spanSize(sortedCols, colIndex, internalColFields.length - colFieldIndex - 1) !== 0"
                    :key="JSON.stringify(col)"
                    :colspan="spanSize(sortedCols, colIndex, internalColFields.length - colFieldIndex - 1)"
                  >
                    <slot :name="footerSlotName" :value="col[internalColFields.length - colFieldIndex - 1]">
                      Missing slot <code>{{ footerSlotName }}</code>
                    </slot>
                  </th>
                  <template v-if="!isColumnValues && (rowFieldsWithTotalsForRight.length || showGrandTotalColumn)">
                    <td
                      v-for="rt in rowFieldsWithTotalsForRight"
                      :key="'legcf-' + String(footerSlotName) + '-' + rt.field.key"
                      class="text-right align-middle"
                    >
                      <template v-if="$scopedSlots.value && shouldRenderFooterValueSlot(classicPivotGrandTotalRaw)">
                        <slot
                          name="value"
                          :value="classicPivotGrandTotalRaw"
                          :row="[]"
                          :col="[]"
                          :isColumnValues="false"
                        />
                      </template>
                      <template v-else>
                        {{ formatFooterScalar(classicPivotGrandTotalRaw) }}
                      </template>
                    </td>
                    <td
                      v-if="showGrandTotalColumn"
                      :key="'legcfgt-' + String(footerSlotName)"
                      class="text-right align-middle"
                    >
                      <template v-if="$scopedSlots.value && shouldRenderFooterValueSlot(classicPivotGrandTotalRaw)">
                        <slot
                          name="value"
                          :value="classicPivotGrandTotalRaw"
                          :row="[]"
                          :col="[]"
                          :isColumnValues="false"
                        />
                      </template>
                      <template v-else>
                        {{ formatFooterScalar(classicPivotGrandTotalRaw) }}
                      </template>
                    </td>
                  </template>
                  <!-- Bottom right dead zone -->
                  <th
                    v-if="colFieldIndex === firstColFieldFooterIndex && rowFooterSize > 0 && footerSlotNameIndex === 0"
                    :colspan="rowFooterSize"
                    :rowspan="colFooterSize"
                  />
                </tr>
              </template>
              <!-- Single slot/no slot -->
              <tr v-else>
                <!-- Bottom left dead zone -->
                <th
                  v-if="colFieldIndex === firstColFieldFooterIndex && rowHeaderSize > 0"
                  :colspan="rowHeaderSize"
                  :rowspan="colHeaderSize"
                />
                <!-- Column footers -->
                <th
                  v-for="(col, colIndex) in sortedCols"
                  v-if="spanSize(sortedCols, colIndex, internalColFields.length - colFieldIndex - 1) !== 0"
                  :key="JSON.stringify(col)"
                  :colspan="spanSize(sortedCols, colIndex, internalColFields.length - colFieldIndex - 1)"
                >
                  <slot v-if="colField.footerSlotName" :name="colField.footerSlotName" :value="col[internalColFields.length - colFieldIndex - 1]">
                    Missing slot <code>{{ colField.footerSlotName }}</code>
                  </slot>
                  <template v-else>
                    {{ col[internalColFields.length - colFieldIndex - 1] }}
                  </template>
                </th>
                <template v-if="!isColumnValues && (rowFieldsWithTotalsForRight.length || showGrandTotalColumn)">
                  <td
                    v-for="rt in rowFieldsWithTotalsForRight"
                    :key="'legcf2-' + rt.field.key + '-' + colField.key"
                    class="text-right align-middle"
                  >
                    <template v-if="$scopedSlots.value && shouldRenderFooterValueSlot(classicPivotGrandTotalRaw)">
                      <slot
                        name="value"
                        :value="classicPivotGrandTotalRaw"
                        :row="[]"
                        :col="[]"
                        :isColumnValues="false"
                      />
                    </template>
                    <template v-else>
                      {{ formatFooterScalar(classicPivotGrandTotalRaw) }}
                    </template>
                  </td>
                  <td
                    v-if="showGrandTotalColumn"
                    :key="'legcf2gt-' + colField.key"
                    class="text-right align-middle"
                  >
                    <template v-if="$scopedSlots.value && shouldRenderFooterValueSlot(classicPivotGrandTotalRaw)">
                      <slot
                        name="value"
                        :value="classicPivotGrandTotalRaw"
                        :row="[]"
                        :col="[]"
                        :isColumnValues="false"
                      />
                    </template>
                    <template v-else>
                      {{ formatFooterScalar(classicPivotGrandTotalRaw) }}
                    </template>
                  </td>
                </template>
                <!-- Bottom right dead zone -->
                <th
                  v-if="colFieldIndex === firstColFieldFooterIndex && rowFooterSize > 0"
                  :colspan="rowFooterSize"
                  :rowspan="colFooterSize"
                />
              </tr>
            </template>

            <!-- Column-dimension subtotals (classic pivot only); after legacy col field footers -->
            <template v-if="!isColumnValues && colTotalsFooterRows.length">
              <tr
                v-for="footerRow in colTotalsFooterRows"
                :key="footerRow.key"
                class="pivot-col-totals-row"
              >
                <th
                  :colspan="rowHeaderSize > 0 ? rowHeaderSize : 1"
                  class="text-left font-weight-bold align-middle"
                >
                  {{ totalByFieldLabelPrefix }}{{ footerRow.field.label }}
                </th>
                <td
                  v-for="cell in footerRow.cells"
                  :key="cell.key"
                  :colspan="cell.colspan"
                  class="text-right align-middle"
                >
                  <template v-if="$scopedSlots.value && shouldRenderFooterValueSlot(cell.rawValue)">
                    <slot
                      name="value"
                      :value="cell.rawValue"
                      :row="[]"
                      :col="cell.colValues"
                      :isColumnValues="false"
                    />
                  </template>
                  <template v-else>
                    {{ formatFooterScalar(cell.rawValue) }}
                  </template>
                </td>
                <template v-if="rowFieldsWithTotalsForRight.length || showGrandTotalColumn">
                  <td
                    v-for="rt in rowFieldsWithTotalsForRight"
                    :key="'ctf-' + footerRow.key + '-' + rt.field.key"
                    class="text-right align-middle"
                  >
                    <template v-if="$scopedSlots.value && shouldRenderFooterValueSlot(classicPivotGrandTotalRaw)">
                      <slot
                        name="value"
                        :value="classicPivotGrandTotalRaw"
                        :row="[]"
                        :col="[]"
                        :isColumnValues="false"
                      />
                    </template>
                    <template v-else>
                      {{ formatFooterScalar(classicPivotGrandTotalRaw) }}
                    </template>
                  </td>
                  <td
                    v-if="showGrandTotalColumn"
                    :key="'ctfgt-' + footerRow.key"
                    class="text-right align-middle"
                  >
                    <template v-if="$scopedSlots.value && shouldRenderFooterValueSlot(classicPivotGrandTotalRaw)">
                      <slot
                        name="value"
                        :value="classicPivotGrandTotalRaw"
                        :row="[]"
                        :col="[]"
                        :isColumnValues="false"
                      />
                    </template>
                    <template v-else>
                      {{ formatFooterScalar(classicPivotGrandTotalRaw) }}
                    </template>
                  </td>
                </template>
                <th
                  v-if="rowFooterSize > 0"
                  :colspan="rowFooterSize"
                  class="pivot-col-totals-row__row-footer-gap"
                />
              </tr>
            </template>

            <!-- Bottom row: full-grid total under value area when no column-dimension footer rows -->
            <tr
              v-if="needsStandaloneGrandTotalFooterRow"
              :key="'pivot-grand-total-footer'"
              class="pivot-grand-total-footer-row"
            >
              <th
                :colspan="rowHeaderSize > 0 ? rowHeaderSize : 1"
                class="text-left font-weight-bold align-middle"
              >
                {{ grandTotalLabel }}
              </th>
              <td
                v-for="(col, colIndex) in sortedCols"
                v-if="internalColFields.length && spanSize(sortedCols, colIndex, lastColFieldIndexForSpan) !== 0"
                :key="'sgtf-' + colIndex + '-' + JSON.stringify(col)"
                :colspan="spanSize(sortedCols, colIndex, lastColFieldIndexForSpan)"
                class="text-right align-middle"
              >
                &nbsp;
              </td>
              <template v-if="rowFieldsWithTotalsForRight.length || showGrandTotalColumn">
                <td
                  v-for="rt in rowFieldsWithTotalsForRight"
                  :key="'sgtf-rt-' + rt.field.key"
                  class="text-right align-middle"
                >
                  <template v-if="$scopedSlots.value && shouldRenderFooterValueSlot(classicPivotGrandTotalRaw)">
                    <slot
                      name="value"
                      :value="classicPivotGrandTotalRaw"
                      :row="[]"
                      :col="[]"
                      :isColumnValues="false"
                    />
                  </template>
                  <template v-else>
                    {{ formatFooterScalar(classicPivotGrandTotalRaw) }}
                  </template>
                </td>
                <td
                  v-if="showGrandTotalColumn"
                  :key="'sgtf-gt'"
                  class="text-right align-middle"
                >
                  <template v-if="$scopedSlots.value && shouldRenderFooterValueSlot(classicPivotGrandTotalRaw)">
                    <slot
                      name="value"
                      :value="classicPivotGrandTotalRaw"
                      :row="[]"
                      :col="[]"
                      :isColumnValues="false"
                    />
                  </template>
                  <template v-else>
                    {{ formatFooterScalar(classicPivotGrandTotalRaw) }}
                  </template>
                </td>
              </template>
              <th
                v-if="rowFooterSize > 0"
                :colspan="rowFooterSize"
                class="pivot-grand-total-footer-row__row-footer-gap"
              />
            </tr>

            <!-- Column-values mode: single footer row (per metric), no row/col dimension subtotals -->
            <tr
              v-if="showColumnValuesTotalsFooter"
              :key="'pivot-column-values-totals'"
              class="pivot-column-values-totals-row"
            >
              <th
                :colspan="rowHeaderSize > 0 ? rowHeaderSize : 1"
                class="text-left font-weight-bold align-middle"
              >
                {{ grandTotalLabel }}
              </th>
              <td
                v-for="vc in safeValueColumns"
                :key="'cvtot-' + vc.key"
                class="text-right align-middle"
              >
                <template v-if="$scopedSlots.value && shouldRenderFooterValueSlot(columnValuesCombinedFooterRaw)">
                  <slot
                    name="value"
                    :value="columnValuesCombinedFooterRaw"
                    :row="[]"
                    :col="vc"
                    :isColumnValues="true"
                  />
                </template>
                <template v-else>
                  {{ formatFooterScalar(columnValuesCombinedFooterRaw) }}
                </template>
              </td>
              <td
                v-for="extra in columnValuesMetricRowTotalColumns"
                :key="'cvtot-extra-' + extra.key"
                class="text-right align-middle"
              >
                <template v-if="$scopedSlots.value && shouldRenderFooterValueSlot(columnValuesCombinedFooterRaw)">
                  <slot
                    name="value"
                    :value="columnValuesCombinedFooterRaw"
                    :row="[]"
                    :col="extra.metric"
                    :isColumnValues="true"
                  />
                </template>
                <template v-else>
                  {{ formatFooterScalar(columnValuesCombinedFooterRaw) }}
                </template>
              </td>
              <th
                v-if="rowFooterSize > 0"
                :colspan="rowFooterSize"
                class="pivot-column-values-totals-row__row-footer-gap"
              />
            </tr>
          </tfoot>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import { firstBy } from 'thenby';
import naturalSort from 'javascript-natural-sort';
import { cloneDeep, isEqual } from 'lodash';
import HashTable from './HashTable';

export default {
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    rowFields: {
      type: Array,
      default: () => [],
    },
    colFields: {
      type: Array,
      default: () => [],
    },
    reducer: {
      type: Function,
      default: (sum, item) => sum + 1,
    },
    noDataWarningText: {
      type: String,
      default: 'No data to display.',
    },
    isDataLoading: {
      type: Boolean,
      default: false,
    },
    valueColumns: {
      type: Array,
      default: () => [],
    },
    isColumnValues: {
      type: Boolean,
      default: false,
    },
    /** Row dimension keys that have "totals" enabled (constructor order); used by totals UI (stage 2+). */
    rowFieldKeysForTotals: {
      type: Array,
      default: () => [],
    },
    /** Column dimension keys that have "totals" enabled (constructor order); used by totals UI (stage 2+). */
    colFieldKeysForTotals: {
      type: Array,
      default: () => [],
    },
    /** Master switch: when false, no automatic totals in the table (phase 2). */
    totalsMasterEnabled: {
      type: Boolean,
      default: false,
    },
    /** Prefix for automatic column-dimension total rows, e.g. "Итого по " + field label. */
    totalByFieldLabelPrefix: {
      type: String,
      default: 'Итого по ',
    },
    /** Header label for the full-grid grand total column (classic pivot, when any totals are enabled). */
    grandTotalLabel: {
      type: String,
      default: 'Всего',
    },
    /** When false, hide the right-hand «Всего» column (classic pivot; master totals must still be on). */
    showGrandTotalColumnEnabled: {
      type: Boolean,
      default: true,
    },
    /** When false, hide the bottom full-grid «Всего» footer row (classic pivot). */
    showGrandTotalRowEnabled: {
      type: Boolean,
      default: true,
    },
    /**
     * Column-values mode: show the bottom «Всего» row (per metric), independent of dimension totals checkboxes.
     */
    columnValuesFooterTotalsEnabled: {
      type: Boolean,
      default: true,
    },
    /** Joiner between metric label and row dimension in extra headers, e.g. " итого по ". */
    columnValuesMetricRowTotalJoiner: {
      type: String,
      default: ' итого по ',
    },
  },
  data() {
    return {
      valuesHashTable: null,
      rows: [],
      cols: [],
      // Note: we don't use directly rowFields/colFields props to trigger table render when `updateValues` has finished
      internalRowFields: [],
      internalColFields: [],
      isDataComputing: false,
      computingInterval: null,
    };
  },
  computed: {
    // Sort rows/cols using a composed function built with thenBy.js
    sortedRows() {
      let composedSortFunction;
      this.internalRowFields.forEach((rowField, rowFieldIndex) => {
        if (rowFieldIndex === 0) {
          composedSortFunction = firstBy(0, { cmp: rowField.sort || naturalSort });
        } else {
          composedSortFunction = composedSortFunction.thenBy(rowFieldIndex, { cmp: rowField.sort || naturalSort });
        }
      });

      return [...this.rows].sort(composedSortFunction);
    },
    sortedCols() {
      let composedSortFunction;
      this.internalColFields.forEach((colField, colFieldIndex) => {
        if (colFieldIndex === 0) {
          composedSortFunction = firstBy(0, { cmp: colField.sort || naturalSort });
        } else {
          composedSortFunction = composedSortFunction.thenBy(colFieldIndex, { cmp: colField.sort || naturalSort });
        }
      });

      return [...this.cols].sort(composedSortFunction);
    },
    /** Valid metric columns only (avoids stale keys from localStorage producing undefined entries). */
    safeValueColumns() {
      return (this.valueColumns || []).filter((c) => c && c.key != null);
    },
    // Property to watch specific field changes
    fields() {
      return [
        [this.colFields, this.rowFields],
        [this.colFields.map((field) => field.headerSlotNames), this.rowFields.map((field) => field.headerSlotNames)],
        [this.colFields.map((field) => field.valuesFiltered), this.rowFields.map((field) => field.valuesFiltered)],
      ];
    },
    // Reversed props for footer iterators
    internalRowFieldsReverse() {
      return this.internalRowFields.slice().reverse();
    },
    internalColFieldsReverse() {
      return this.internalColFields.slice().reverse();
    },
    // Number of row header columns
    rowHeaderSize() {
      return this.internalRowFields.reduce((acc, rowField) => {
        if (rowField.showHeader === undefined || rowField.showHeader) {
          if (rowField.headerSlotNames) return acc + rowField.headerSlotNames.length;
          return acc + 1;
        }
        return acc;
      }, 0);
    },
    // Number of row footer columns
    rowFooterSize() {
      return this.internalRowFields.reduce((acc, rowField) => {
        if (rowField.showFooter) {
          if (rowField.footerSlotNames) return acc + rowField.footerSlotNames.length;
          return acc + 1;
        }
        return acc;
      }, 0);
    },
    // Number of col header rows
    colHeaderSize() {
      return this.internalColFields.reduce((acc, colField) => {
        if (colField.showHeader === undefined || colField.showHeader) {
          if (colField.headerSlotNames) return acc + colField.headerSlotNames.length;
          return acc + 1;
        }
        return acc;
      }, 0);
    },
    // Number of col footer rows
    colFooterSize() {
      return this.internalColFields.reduce((acc, colField) => {
        if (colField.showFooter) {
          if (colField.footerSlotNames) return acc + colField.footerSlotNames.length;
          return acc + 1;
        }
        return acc;
      }, 0);
    },
    // Index of the first column field header to show - used for table header dead zones
    firstColFieldHeaderIndex() {
      return this.internalColFields.findIndex((colField) => colField.showHeader === undefined || colField.showHeader);
    },
    // Index of the first column field footer to show - used for table footer dead zones
    firstColFieldFooterIndex() {
      return this.internalColFieldsReverse.findIndex((colField) => colField.showFooter);
    },
    colFieldsWithTotalsForFooter() {
      if (!this.totalsMasterEnabled || this.isColumnValues) {
        return [];
      }
      return this.colFieldKeysForTotals
        .map((key) => {
          const colFieldIndex = this.internalColFields.findIndex((f) => f.key === key);
          if (colFieldIndex < 0) {
            return null;
          }
          return {
            field: this.internalColFields[colFieldIndex],
            colFieldIndex,
          };
        })
        .filter(Boolean);
    },
    colTotalsFooterRows() {
      if (this.isColumnValues || !this.valuesHashTable || !this.sortedCols.length || !this.sortedRows.length) {
        return [];
      }
      if (!this.colFieldsWithTotalsForFooter.length) {
        return [];
      }
      return this.colFieldsWithTotalsForFooter.map(({ field, colFieldIndex }) => {
        const cells = [];
        for (let colIndex = 0; colIndex < this.sortedCols.length; colIndex += 1) {
          if (this.spanSize(this.sortedCols, colIndex, colFieldIndex) === 0) {
            continue;
          }
          const colspan = this.spanSize(this.sortedCols, colIndex, colFieldIndex);
          const col = this.sortedCols[colIndex];
          const rawValue = this.mergeRawValuesForColSpan(colIndex, colspan);
          cells.push({
            key: `ft-${field.key}-${colIndex}-${JSON.stringify(col)}`,
            colspan,
            colValues: Object.values(col),
            rawValue,
          });
        }
        return {
          key: `col-total-${field.key}`,
          field,
          colFieldIndex,
          cells,
        };
      });
    },
    rowFieldsWithTotalsForRight() {
      if (!this.totalsMasterEnabled || this.isColumnValues) {
        return [];
      }
      return this.rowFieldKeysForTotals
        .map((key) => {
          const rowFieldIndex = this.internalRowFields.findIndex((f) => f.key === key);
          if (rowFieldIndex < 0) {
            return null;
          }
          return {
            field: this.internalRowFields[rowFieldIndex],
            rowFieldIndex,
          };
        })
        .filter(Boolean);
    },
    /** Row dimensions with totals on, for column-values mode extra columns. */
    rowFieldsWithTotalsForColumnValues() {
      if (!this.totalsMasterEnabled || !this.isColumnValues) {
        return [];
      }
      return this.rowFieldKeysForTotals
        .map((key) => {
          const rowFieldIndex = this.internalRowFields.findIndex((f) => f.key === key);
          if (rowFieldIndex < 0) {
            return null;
          }
          return {
            field: this.internalRowFields[rowFieldIndex],
            rowFieldIndex,
          };
        })
        .filter(Boolean);
    },
    /**
     * Column-values mode: extra columns after metrics — for each metric, then each row dimension with totals
     * (constructor order of row fields × order of value columns).
     */
    columnValuesMetricRowTotalColumns() {
      if (!this.totalsMasterEnabled || !this.isColumnValues) {
        return [];
      }
      const rows = this.rowFieldsWithTotalsForColumnValues;
      if (!rows.length || !this.safeValueColumns.length) {
        return [];
      }
      const joiner = this.columnValuesMetricRowTotalJoiner;
      const out = [];
      this.safeValueColumns.forEach((vc) => {
        rows.forEach((rt) => {
          out.push({
            key: `${vc.key}::${rt.field.key}`,
            metric: vc,
            rowFieldIndex: rt.rowFieldIndex,
            field: rt.field,
            headerLabel: `${vc.label}${joiner}${rt.field.label}`,
          });
        });
      });
      return out;
    },
    showGrandTotalColumn() {
      return this.totalsMasterEnabled
        && !this.isColumnValues
        && this.showGrandTotalColumnEnabled
        && (this.rowFieldKeysForTotals.length > 0 || this.colFieldKeysForTotals.length > 0);
    },
    classicPivotGrandTotalRaw() {
      if (!this.totalsMasterEnabled || this.isColumnValues || !this.valuesHashTable || !this.sortedCols.length) {
        return [];
      }
      return this.mergeRawValuesForColSpan(0, this.sortedCols.length);
    },
    /** Per body row: raw values for row-total columns and row-level grand column. */
    bodyRowTotalsMatrix() {
      if (!this.totalsMasterEnabled || this.isColumnValues || !this.sortedRows.length) {
        return [];
      }
      if (!this.valuesHashTable) {
        return this.sortedRows.map(() => ({ rowTotals: [], grandRowRaw: [] }));
      }
      return this.sortedRows.map((row) => ({
        rowTotals: this.rowFieldsWithTotalsForRight.map((rt) => ({
          key: rt.field.key,
          rowFieldIndex: rt.rowFieldIndex,
          rawValue: this.mergeRawValuesForRowPrefix(row.slice(0, rt.rowFieldIndex + 1)),
        })),
        grandRowRaw: this.mergeRawValuesForSingleRow(row),
      }));
    },
    hasLegacyColFieldFooters() {
      return this.internalColFields.some((c) => c.showFooter);
    },
    needsStandaloneGrandTotalFooterRow() {
      return !this.isColumnValues
        && this.totalsMasterEnabled
        && this.showGrandTotalRowEnabled
        && (this.rowFieldKeysForTotals.length > 0 || this.colFieldKeysForTotals.length > 0)
        && !this.colTotalsFooterRows.length
        && !this.hasLegacyColFieldFooters;
    },
    lastColFieldIndexForSpan() {
      return Math.max(0, this.internalColFields.length - 1);
    },
    /**
     * In column-values mode all body cells share one hash bucket per row; merged items are the same
     * for every metric column — ReportConstructor reduceValue uses col.key when isColumnValues.
     */
    columnValuesCombinedFooterRaw() {
      if (!this.totalsMasterEnabled || !this.isColumnValues || !this.valuesHashTable || !this.sortedRows.length) {
        return [];
      }
      const bucket = this.newMergeBucket();
      for (let r = 0; r < this.sortedRows.length; r += 1) {
        const v = this.valuesHashTable.get([...this.sortedRows[r]]);
        this.accumulateMergeBucket(bucket, v);
      }
      return this.finalizeMergeBucket(bucket);
    },
    showColumnValuesTotalsFooter() {
      return this.totalsMasterEnabled
        && this.isColumnValues
        && this.columnValuesFooterTotalsEnabled
        && this.valuesHashTable
        && this.sortedRows.length > 0
        && this.safeValueColumns.length > 0;
    },
  },
  watch: {
    // Check if fields changed
    fields(val, oldVal) {
      if (!isEqual(val[0], oldVal[0])) {
        // Fields were moved
        this.updateValues();
      } else if (!isEqual(val[1], oldVal[1])) {
        // Field header value filters changed
        this.updateInternalFields();
      } else if (!isEqual(val[2], oldVal[2])) {
        // Field header slot names changed
        this.updateValues(false);
      }
    },
    data() {
      this.updateValues();
    },
    isColumnValues() {
      this.updateValues();
    },
    valueColumns: {
      deep: true,
      handler() {
        this.updateValues();
      },
    },
  },
  created() {
    this.updateValues();
  },
  methods: {
    // Get value from valuesHashTable
    value(row, col) {
      return this.valuesHashTable.get([...row, ...col]) || 0;
    },
    newMergeBucket() {
      return { mergedItems: [], numericSum: 0, hasNumeric: false };
    },
    accumulateMergeBucket(bucket, v) {
      if (v == null) {
        return;
      }
      if (Array.isArray(v)) {
        bucket.mergedItems.push(...v);
      } else if (typeof v === 'number' && !Number.isNaN(v)) {
        bucket.numericSum += v;
        bucket.hasNumeric = true;
      }
    },
    finalizeMergeBucket(bucket) {
      if (bucket.mergedItems.length) {
        return bucket.mergedItems;
      }
      if (bucket.hasNumeric) {
        return bucket.numericSum;
      }
      return [];
    },
    mergeRawValuesForColSpan(startColIndex, span) {
      const bucket = this.newMergeBucket();
      for (let r = 0; r < this.sortedRows.length; r += 1) {
        const row = this.sortedRows[r];
        for (let j = startColIndex; j < startColIndex + span; j += 1) {
          const v = this.valuesHashTable.get([...row, ...this.sortedCols[j]]);
          this.accumulateMergeBucket(bucket, v);
        }
      }
      return this.finalizeMergeBucket(bucket);
    },
    mergeRawValuesForRowPrefix(prefixSlice) {
      const bucket = this.newMergeBucket();
      const L = prefixSlice.length;
      for (let r = 0; r < this.sortedRows.length; r += 1) {
        const row = this.sortedRows[r];
        let match = true;
        for (let i = 0; i < L; i += 1) {
          if (row[i] !== prefixSlice[i]) {
            match = false;
            break;
          }
        }
        if (!match) {
          continue;
        }
        for (let c = 0; c < this.sortedCols.length; c += 1) {
          const v = this.valuesHashTable.get([...row, ...this.sortedCols[c]]);
          this.accumulateMergeBucket(bucket, v);
        }
      }
      return this.finalizeMergeBucket(bucket);
    },
    mergeRawValuesForSingleRow(row) {
      const bucket = this.newMergeBucket();
      for (let c = 0; c < this.sortedCols.length; c += 1) {
        const v = this.valuesHashTable.get([...row, ...this.sortedCols[c]]);
        this.accumulateMergeBucket(bucket, v);
      }
      return this.finalizeMergeBucket(bucket);
    },
    /** Column-values mode: merged item lists for all rows sharing the row-key prefix (for metric slot). */
    mergeItemsForColumnValuesRowPrefix(prefixSlice) {
      if (!this.isColumnValues || !this.valuesHashTable || !this.sortedRows.length) {
        return [];
      }
      const merged = [];
      const L = prefixSlice.length;
      for (let r = 0; r < this.sortedRows.length; r += 1) {
        const sr = this.sortedRows[r];
        let match = true;
        for (let i = 0; i < L; i += 1) {
          if (sr[i] !== prefixSlice[i]) {
            match = false;
            break;
          }
        }
        if (!match) {
          continue;
        }
        const v = this.valuesHashTable.get([...sr]);
        if (Array.isArray(v)) {
          merged.push(...v);
        }
      }
      return merged;
    },
    columnValuesMetricRowTotalRaw(rowTuple, rowFieldIndex) {
      return this.mergeItemsForColumnValuesRowPrefix(rowTuple.slice(0, rowFieldIndex + 1));
    },
    isFirstClassicPivotHeaderRow(colFieldIndex, headerSlotNameIndex, colField) {
      if (this.isColumnValues) {
        return false;
      }
      if (colFieldIndex !== this.firstColFieldHeaderIndex) {
        return false;
      }
      if (colField.headerSlotNames && colField.headerSlotNames.length) {
        return headerSlotNameIndex === 0;
      }
      return headerSlotNameIndex === 0;
    },
    shouldRenderFooterValueSlot(rawValue) {
      if (typeof rawValue === 'number' && Number.isFinite(rawValue)) {
        return false;
      }
      return true;
    },
    formatFooterScalar(rawValue) {
      if (rawValue == null || rawValue === '') {
        return '';
      }
      if (Array.isArray(rawValue)) {
        return '';
      }
      return rawValue;
    },
    // Get colspan/rowspan size
    spanSize(values, valueIndex, fieldIndex) {
      // If left value === current value
      // and top value === 0 (= still in the same top bracket)
      // The left td will take care of the display
      if (valueIndex > 0
        && values[valueIndex - 1][fieldIndex] === values[valueIndex][fieldIndex]
        && (fieldIndex === 0 || (this.spanSize(values, valueIndex, fieldIndex - 1) === 0))) {
        return 0;
      }

      // Otherwise, count entries on the right with the same value
      // But stop if the top value !== 0 (= the top bracket has changed)
      let size = 1;
      let i = valueIndex;
      while (i + 1 < values.length
        && values[i + 1][fieldIndex] === values[i][fieldIndex]
        && (fieldIndex === 0 || (i + 1 < values.length && this.spanSize(values, i + 1, fieldIndex - 1) === 0))) {
        i++;
        size++;
      }

      return size;
    },
    // Update rows/cols/valuesHashTable (optional)
    // @param {boolean} updateValuesHashTable
    updateValues(updateValuesHashTable = true) {
      this.isDataComputing = true;

      // Start a task to avoid blocking the page
      clearInterval(this.computingInterval);
      this.computingInterval = setTimeout(() => {
        const rows = [];
        const cols = [];
        const valuesHashTable = new HashTable();

        const fields = [...this.rowFields, ...this.colFields];

        this.data.forEach((item) => {
          const rowKey = [];
          const colKey = [];
          let filtered = false;

          // Check if item passes fields value filters
          for (const field of fields) {
            if (field.valuesFiltered && !field.valuesFiltered.has(field.getter(item))) {
              filtered = true;
              break;
            }
          }

          // Build item rowKey/colKey if necessary
          if (!filtered || updateValuesHashTable) {
            this.rowFields.forEach((field) => {
              rowKey.push(field.getter(item));
            });

            this.colFields.forEach((field) => {
              colKey.push(field.getter(item));
            });
          }

          // Update rows/cols
          if (!filtered) {
            if (!rows.some((row) => this.rowFields.every((rowField, index) => row[index] === rowKey[index]))) {
              rows.push(rowKey);
            }

            if (!cols.some((col) => this.colFields.every((colField, index) => col[index] === colKey[index]))) {
              cols.push(colKey);
            }
          }

          // Update valuesHashTable
          if (updateValuesHashTable) {
            const key = [...rowKey, ...colKey];

            const previousValue = valuesHashTable.get(key) || 0;

            valuesHashTable.set(key, this.reducer(previousValue, item));
          }
        });

        this.rows = rows;
        this.cols = cols;
        if (updateValuesHashTable) this.valuesHashTable = valuesHashTable;
        this.isDataComputing = false;
        this.updateInternalFields();
      }, 0);
    },
    // Update internal fields only
    updateInternalFields() {
      this.internalRowFields = cloneDeep(this.rowFields);
      this.internalColFields = cloneDeep(this.colFields);
    },
  },
};
</script>

<style scoped>
td {
  min-width: 100px;
}
</style>
